/**
 * Deposit resolution — single source of truth for both the customer
 * booking wizard and the marketing landing preview.
 *
 * Precedence (matches add-deposit-percentage.sql):
 *   1. service.deposit_mode = 'none'    -> no deposit
 *   2. service.deposit_mode = 'custom'  -> use service's own type + amount
 *   3. otherwise (or 'inherit')         -> use shop.deposit_required + type + amount
 *
 * Percentages ALWAYS round UP to whole £ (Math.ceil). This protects the
 * barber — a 20% deposit on a £22 cut = £4.40 → £5.
 *
 * Returns { amount, required, allowPayInFull, fullAmount } where
 * `amount` is the deposit (or 0), `required` mirrors the boolean the
 * wizard uses to route through Stripe, `allowPayInFull` reflects the
 * per-service opt-in, and `fullAmount` is the total service price so
 * the "pay in full" option can render without a second lookup.
 */
export function resolveDeposit(service, shop) {
  if (!service || !shop) return { amount: 0, required: false, allowPayInFull: false, fullAmount: 0 };

  const price = Number(service.price || 0);
  const allowPayInFull = !!service.allow_pay_in_full;
  const base = { allowPayInFull, fullAmount: price };

  // 1. Per-service explicit override wins.
  if (service.deposit_mode === 'none') {
    return { amount: 0, required: false, ...base };
  }

  if (service.deposit_mode === 'custom') {
    return { ...resolveFromConfig(service.deposit_type, service.deposit_amount, price), ...base };
  }

  // 2. Fall back to shop-wide setting.
  if (!shop.deposit_required) {
    return { amount: 0, required: false, ...base };
  }

  return { ...resolveFromConfig(shop.deposit_type, shop.deposit_amount, price), ...base };
}

function resolveFromConfig(type, rawAmount, servicePrice) {
  const value = Number(rawAmount || 0);
  if (value <= 0) return { amount: 0, required: false };

  if (type === 'percent') {
    const pounds = Math.ceil((servicePrice * value) / 100);
    if (pounds <= 0) return { amount: 0, required: false };
    return { amount: pounds, required: true };
  }

  // Default: fixed £
  return { amount: value, required: true };
}
